import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DownloadCloud, Loader2, RefreshCcw, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export const Section = ({ testid, children, className = "" }) => (
  <section className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 ${className}`} data-testid={testid}>
    {children}
  </section>
);

/** Run the review-gated Intercom import now and follow it to the end. */
function RunNowCard({ status, onDone }) {
  const [importing, setImporting] = useState(status?.status === "running");

  const run = async () => {
    if (importing) return;
    setImporting(true);
    const t = toast.loading("Starting Intercom import…");
    try {
      await api.post("/kb/import/intercom");
      toast.loading("Importing Intercom internal articles… (runs in the background)", { id: t });
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const { data: s } = await api.get("/kb/import/intercom/status");
          if (s.status !== "running" || tries > 40) {
            clearInterval(poll);
            setImporting(false);
            if (s.status === "error") toast.error("Import failed — please try again", { id: t });
            else {
              const changed = (s.changed ?? 0) + (s.snippets_changed ?? 0);
              const snip = s.snippets_status === "blocked" ? " · snippets need the Intercom content scope"
                : (s.snippets_new ? ` · ${s.snippets_new} snippets (review)` : "");
              toast.success(`${s.imported ?? 0} new (review) · ${s.skipped ?? 0} already in KB${changed ? ` · ${changed} changed (review)` : ""}${snip}`, { id: t });
              if ((s.imported ?? 0) + changed) toast.info("Nothing was added automatically — open the Import review tab to include new articles and apply changes");
            }
            onDone?.(s);
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      setImporting(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed", { id: t });
    }
  };

  const last = status || {};
  return (
    <Section testid="kb-intercom-run">
      <div className="flex flex-wrap items-center gap-3">
        <DownloadCloud className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100">Import now</h3>
        <Button size="sm" className="ml-auto" onClick={run} disabled={importing} data-testid="kb-intercom-import-now">
          {importing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <DownloadCloud className="mr-1 h-4 w-4" />}
          {importing ? "Importing…" : "Import Intercom"}
        </Button>
      </div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Pulls every Intercom internal article and content snippet. Nothing lands in the KB on its own: new items
        and items whose words changed in Intercom are parked in Import review for you to include or apply, and
        nothing you hold is written over.
      </p>
      {last.at && (
        <p className="mt-2 text-[13px] text-slate-500 dark:text-slate-400" data-testid="kb-intercom-last-run">
          Last run {new Date(last.at).toLocaleString()}: {last.imported ?? 0} new (review) · {last.changed ?? 0} changed (review) · {last.skipped ?? 0} unchanged
          {last.snippets_new || last.snippets_changed ? <> · snippets {last.snippets_new ?? 0} new / {last.snippets_changed ?? 0} changed (review)</> : null}
        </p>
      )}
    </Section>
  );
}

function NightlyCard() {
  const [nightly, setNightly] = useState(null);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => { api.get("/kb/intercom/nightly").then(({ data }) => setNightly(data)).catch(() => {}); }, []);

  const toggle = async (enabled) => {
    setNightly((n) => ({ ...(n || {}), enabled }));
    try {
      const { data } = await api.post("/kb/intercom/nightly", { enabled });
      setNightly(data);
      toast.success(enabled ? "Nightly Intercom sync is on (02:30 MT)" : "Nightly Intercom sync paused");
    } catch { toast.error("Couldn't save that"); }
  };
  const syncNow = async () => {
    setSyncing(true);
    toast.message("Syncing from Intercom — this takes a minute or two…");
    try {
      const { data } = await api.post("/kb/intercom/nightly/run");
      setNightly(data.state);
      const r = data.result || {};
      toast[r.status === "ok" ? "success" : "error"](r.status === "ok"
        ? `Synced — ${r.imported || 0} new articles, ${r.changed || 0} changed (review), ${r.snippets_new || 0} new snippets`
        : `Sync didn't complete (${r.status || r.skipped || "error"})`);
    } catch { toast.error("Sync failed — check the backend logs"); }
    finally { setSyncing(false); }
  };

  return (
    <Section testid="kb-nightly-sync" className="mt-4">
      <div className="flex flex-wrap items-center gap-3">
        <RefreshCcw className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100">Nightly Intercom sync</h3>
        <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">02:30 Mountain Time</span>
        <span className="ml-auto flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing} data-testid="kb-nightly-sync-now">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Sync now
          </Button>
          <Switch checked={nightly?.enabled !== false} onCheckedChange={toggle} data-testid="kb-nightly-toggle" />
        </span>
      </div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Re-pulls Intercom internal articles and content snippets every night with the same rules —
        new items and edits made in Intercom show up in Import review the next morning; nothing is added, duplicated or overwritten on its own.
      </p>
      <p className="mt-2 text-[13px] text-slate-500 dark:text-slate-400" data-testid="kb-nightly-status">
        {nightly?.last_run
          ? <>Last run {new Date(nightly.last_run).toLocaleString()} — <span className={nightly.last_status === "ok" ? "font-medium text-emerald-700" : "font-medium text-amber-700"}>{nightly.last_status}</span>
              {nightly.last_status === "ok" && <> · {nightly.articles_new || 0} new articles · {nightly.snippets_new || 0} new snippets</>}
              {nightly.runs ? <> · {nightly.runs} run{nightly.runs === 1 ? "" : "s"} so far</> : null}</>
          : "Hasn't run yet — it'll fire tonight, or hit Sync now."}
      </p>
    </Section>
  );
}

/** Settings → Knowledge Base → Intercom: import now + the nightly schedule. */
export const KbIntercomImportPanel = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/kb/import/intercom/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="kb-intercom-panel">
      <PageHeader compact title="Intercom"
        subtitle="Intercom internal articles and content snippets — pulled on demand or nightly; new items and changes wait in Import review before they land" />
      <RunNowCard status={status} onDone={setStatus} />
      <NightlyCard />
    </div>
  );
};
